import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Megaphone, Plus, Pencil, Trash2, Loader2, Save } from "lucide-react";

const COLOR_OPTIONS = [
  { value: "teal", label: "Teal (General)" },
  { value: "red", label: "Red (Urgent)" },
  { value: "green", label: "Green (Offers)" },
  { value: "blue", label: "Blue (Info)" },
];

export default function AdminTickers() {
  const [tickers, setTickers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchTickers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/tickers");
      setTickers(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickers(); }, []);

  const saveTicker = async () => {
    setSaving(true);
    try {
      if (edit.id) {
        await api.put(`/admin/tickers/${edit.id}`, edit);
      } else {
        await api.post("/admin/tickers", edit);
      }
      setEdit(null);
      fetchTickers();
      toast.success("Ticker saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const deleteTicker = async (id) => {
    if (!window.confirm("Delete this ticker?")) return;
    await api.delete(`/admin/tickers/${id}`);
    fetchTickers();
    toast.success("Ticker deleted");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-tickers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-primary" />
            Announcements
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Manage scrolling ticker announcements</p>
        </div>
        <Button
          onClick={() => setEdit({ text: "", link_url: "", link_text: "", color: "teal", priority: 0, is_active: true })}
          data-testid="add-ticker-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New Announcement
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />)}</div>
      ) : tickers.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">
          <Megaphone className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No announcements yet</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="ticker-list">
          {tickers.map((t) => (
            <Card key={t.id} className="p-4" data-testid={`ticker-row-${t.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium">{t.text}</span>
                    <Badge variant={t.is_active ? "default" : "secondary"} className="text-[10px]">
                      {t.is_active ? "Active" : "Inactive"}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] capitalize">{t.color}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Priority: {t.priority}
                    {t.link_url && ` • Links to: ${t.link_url}`}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEdit({ ...t })} data-testid={`edit-ticker-${t.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteTicker(t.id)} data-testid={`delete-ticker-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Sheet */}
      <Sheet open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <SheetContent className="sm:w-[480px] overflow-y-auto" data-testid="ticker-edit-sheet">
          <SheetHeader>
            <SheetTitle>{edit?.id ? "Edit Announcement" : "New Announcement"}</SheetTitle>
          </SheetHeader>
          {edit && (
            <div className="mt-4 space-y-4">
              <div>
                <Label>Ticker Text *</Label>
                <Input
                  value={edit.text}
                  onChange={(e) => setEdit({ ...edit, text: e.target.value })}
                  placeholder="e.g. Flash Sale: 20% off all Kashmir tours!"
                  data-testid="ticker-text-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Link URL</Label>
                  <Input
                    value={edit.link_url}
                    onChange={(e) => setEdit({ ...edit, link_url: e.target.value })}
                    placeholder="/kashmir-group-tour"
                    data-testid="ticker-link-url"
                  />
                </div>
                <div>
                  <Label>Link Text</Label>
                  <Input
                    value={edit.link_text}
                    onChange={(e) => setEdit({ ...edit, link_text: e.target.value })}
                    placeholder="Book Now"
                    data-testid="ticker-link-text"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Color / Urgency</Label>
                  <Select value={edit.color} onValueChange={(v) => setEdit({ ...edit, color: v })}>
                    <SelectTrigger data-testid="ticker-color-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COLOR_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Input
                    type="number"
                    value={edit.priority}
                    onChange={(e) => setEdit({ ...edit, priority: parseInt(e.target.value) || 0 })}
                    data-testid="ticker-priority"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="datetime-local"
                    value={edit.start_date || ""}
                    onChange={(e) => setEdit({ ...edit, start_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                    data-testid="ticker-start-date"
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="datetime-local"
                    value={edit.end_date || ""}
                    onChange={(e) => setEdit({ ...edit, end_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                    data-testid="ticker-end-date"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={edit.is_active} onCheckedChange={(v) => setEdit({ ...edit, is_active: v })} data-testid="ticker-active-switch" />
                <Label>Active</Label>
              </div>
              <Button onClick={saveTicker} disabled={saving || !edit.text} className="w-full" data-testid="ticker-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Save Announcement
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
